import {
  type HeroGs1ComponentStatuses,
  type HeroGs1GroupStatuses,
  type HeroScoreBreakdown,
  type HeroScoringRow,
  type HeroScoringSection,
  toLikertStatus,
  toRowStatus,
  toSectionStatus,
} from './types';

const FOUR_WS_ROWS: ReadonlyArray<{ key: keyof HeroScoreBreakdown; label: string }> = [
  { key: 'four_ws.brand', label: 'Brand' },
  { key: 'four_ws.product_type', label: 'Product type' },
  { key: 'four_ws.variant', label: 'Variant' },
  { key: 'four_ws.size_and_count', label: 'Size & count' },
];

const IMAGE_QUALITY_ROWS: ReadonlyArray<{ key: keyof HeroScoreBreakdown; label: string }> = [
  { key: 'image_quality.background_separation', label: 'Background separation' },
  { key: 'image_quality.product_centered', label: 'Product centering' },
  { key: 'image_quality.product_prominent', label: 'Product prominence' },
  { key: 'image_quality.product_cropping', label: 'Edge proximity' },
  { key: 'image_quality.product_tilt', label: 'Product tilt' },
  { key: 'image_quality.no_off_pack_text', label: 'Off product text' },
];

function toRows(
  componentStatuses: HeroGs1ComponentStatuses | null | undefined,
  spec: ReadonlyArray<{ key: keyof HeroScoreBreakdown; label: string }>,
): HeroScoringRow[] {
  return spec.map(({ key, label }) => ({
    label,
    status: toRowStatus(componentStatuses?.[key]),
  }));
}

/** An image's GS1 payload, as served by the API. */
interface HeroScoringSource {
  /** `hero_gs1_components` — read only for the two likert bar values. */
  scores: HeroScoreBreakdown;
  /** `hero_gs1_component_statuses` — the per-row verdicts. */
  componentStatuses?: HeroGs1ComponentStatuses | null;
  /** `hero_gs1_group_statuses` — the section badges. */
  groupStatuses?: HeroGs1GroupStatuses | null;
}

interface BuildHeroScoringSectionsOptions {
  /**
   * Whether the hero was scored without a product title, so its 4Ws were inferred from the image
   * rather than matched against a title. Adds the accuracy caveat to Shopper Clarity.
   *
   * @default false
   */
  isTitleless?: boolean;
}

/**
 * Builds the ordered {@link HeroScoringSection} list for {@link HeroScoringPanel} from an image's GS1 payload.
 *
 * Produces four sections in a fixed order: two compliance (`rows`) sections — 4Ws and Image Quality (both
 * open by default) — followed by two `score` sections — Composition and Product Staging (the two L2
 * audience-model outputs). Every status is served by the API; only the two likert bar values come off the
 * raw scores. `defaultOpen` is the first-visit fallback only — once the user toggles a section, that choice
 * is persisted and re-applied across hero pages.
 *
 * @param source - See {@link HeroScoringSource}.
 * @param options - See {@link BuildHeroScoringSectionsOptions}.
 * @returns The sections to pass to {@link HeroScoringPanel}.
 */
function buildHeroScoringSections(
  source: HeroScoringSource,
  options: BuildHeroScoringSectionsOptions = {},
): HeroScoringSection[] {
  const { scores, componentStatuses, groupStatuses } = source;
  const { isTitleless = false } = options;

  return [
    {
      id: 'four_ws',
      title: 'Shopper Clarity',
      description: 'Can shopper understand this thumbnail at a glance?',
      status: toSectionStatus(groupStatuses?.shopper_clarity),
      body: { kind: 'rows', rows: toRows(componentStatuses, FOUR_WS_ROWS) },
      defaultOpen: true,
      notice: isTitleless ? 'collection-accuracy' : undefined,
    },
    {
      id: 'image_quality',
      title: 'Image Quality',
      description: 'Is the image well-executed and professionally produced?',
      status: toSectionStatus(groupStatuses?.image_quality),
      body: { kind: 'rows', rows: toRows(componentStatuses, IMAGE_QUALITY_ROWS) },
      defaultOpen: true,
    },
    {
      id: 'composition',
      title: 'Composition',
      description: "Does this image's visual approach look similar to top performers?",
      body: { kind: 'score', score: scores.composition ?? null, status: toLikertStatus(groupStatuses?.composition) },
    },
    {
      id: 'layout_staging',
      title: 'Product Staging',
      description: 'Are key elements positioned and sized effectively?',
      body: {
        kind: 'score',
        score: scores.layout_staging ?? null,
        status: toLikertStatus(groupStatuses?.product_staging),
      },
    },
  ];
}

export { buildHeroScoringSections };
export type { BuildHeroScoringSectionsOptions, HeroScoringSource };
